<template>
  <div class="settings farm-contact-search">
    <div class="main-wrapper">
      <layout-header></layout-header>
      <layout-sidebar></layout-sidebar>

      <div class="page-wrapper">
        <div class="content container-fluid">
          <div class="page-header farm-page-header">
            <div class="row align-items-center">
              <div class="col">
                <h3 class="page-title">Panen Kontak</h3>
                <p class="farm-page-subtitle">
                  Temukan kontak bisnis di sekitar lokasi pilihan Anda.
                </p>
              </div>
              <div class="col-auto farm-header-actions">
                <button type="button" class="btn btn-white" @click="tourShow">
                  <i class="fa fa-question-circle"></i> Panduan
                </button>
                <router-link to="/farm-contact/list" class="btn btn-white">
                  <i class="fa fa-list"></i> Lihat Hasil
                </router-link>
              </div>
            </div>
          </div>

          <div class="farm-layout">
            <div class="farm-map-card">
              <div class="farm-card-title-row">
                <h4 class="farm-card-title">Lokasi Pencarian</h4>
                <span v-if="mapReady && locationSelected" class="farm-selected-location">
                  <i class="fa fa-map-marker"></i>
                  <span class="farm-selected-location-text">{{ locationLabel }}</span>
                </span>
              </div>

              <div class="farm-map-area" data-tour-step="farm-location">
                <div v-if="apiKeyLoading || mapLoading" class="farm-map-state is-loading">
                  <i class="fa fa-spinner fa-spin"></i>
                  <h5>Memuat Google Maps...</h5>
                  <p>Menyiapkan peta dengan API Key yang tersimpan.</p>
                </div>

                <div v-else-if="apiKeyError" class="farm-map-state is-error">
                  <i class="fa fa-exclamation-circle"></i>
                  <h5>Status API Key tidak dapat dimuat</h5>
                  <p>{{ apiKeyError }}</p>
                  <button type="button" class="btn btn-primary btn-sm" @click="loadApiKey">
                    <i class="fa fa-refresh"></i> Coba lagi
                  </button>
                </div>

                <div v-else-if="!apiKeyConfigured" class="farm-map-state is-warning">
                  <i class="fa fa-key"></i>
                  <h5>Google Maps API Key belum diatur</h5>
                  <p>Atur API Key milik Anda untuk menampilkan peta dan memilih lokasi pencarian.</p>
                  <router-link to="/gmapkey" class="btn btn-primary btn-sm">
                    Atur API Key
                  </router-link>
                </div>

                <div v-else-if="mapAuthFailed" class="farm-map-state is-error">
                  <i class="fa fa-lock"></i>
                  <h5>Google Maps menolak API Key</h5>
                  <p>
                    Periksa Maps JavaScript API, Places API, status billing, dan HTTP referrer
                    restrictions pada Google Cloud untuk API Key ini.
                  </p>
                  <div class="farm-map-state-actions">
                    <router-link to="/gmapkey" class="btn btn-primary btn-sm">
                      Ubah API Key
                    </router-link>
                    <button type="button" class="btn btn-white btn-sm" @click="reloadPage">
                      <i class="fa fa-refresh"></i> Muat ulang
                    </button>
                  </div>
                </div>

                <div v-else-if="mapError" class="farm-map-state is-error">
                  <i class="fa fa-exclamation-triangle"></i>
                  <h5>Google Maps gagal dimuat</h5>
                  <p>{{ mapError }}</p>
                  <div class="farm-map-state-actions">
                    <button
                      v-if="mapErrorCode !== 'key-changed'"
                      type="button"
                      class="btn btn-primary btn-sm"
                      @click="retryMapLoad"
                    >
                      <i class="fa fa-refresh"></i> Coba lagi
                    </button>
                    <button
                      v-else
                      type="button"
                      class="btn btn-primary btn-sm"
                      @click="reloadPage"
                    >
                      <i class="fa fa-refresh"></i> Muat ulang halaman
                    </button>
                  </div>
                </div>

                <template v-else-if="mapReady">
                  <div class="farm-map-wrap">
                    <GmapMap
                      :center="center"
                      :zoom="12"
                      map-style-id="roadmap"
                      :options="mapOptions"
                      ref="mapRef"
                      @click="handleMapClick"
                      class="farm-map"
                    >
                      <GmapMarker
                        :position="marker.position"
                        :clickable="true"
                        :draggable="true"
                        @click="panToMarker"
                        @drag="handleMarkerDrag"
                      />
                      <GmapCircle
                        :center="marker.position"
                        :radius="radiusInMeters"
                        :visible="true"
                        :options="{ fillColor: '#FF9B44', fillOpacity: 0.2, strokeColor: '#FF9B44' }"
                      ></GmapCircle>
                    </GmapMap>
                  </div>

                  <p class="farm-map-help">
                    Klik peta atau tarik marker untuk memilih titik pusat pencarian.
                  </p>

                  <div class="farm-location-search">
                    <GmapAutocomplete
                      single-line
                      outlined
                      placeholder="Cari lokasi atau alamat"
                      class="form-control farm-autocomplete"
                      @place_changed="setPlace"
                    />
                    <button
                      type="button"
                      class="btn btn-white farm-location-button"
                      :disabled="locating"
                      @click="useCurrentLocation"
                    >
                      <i class="fa" :class="locating ? 'fa-spinner fa-spin' : 'fa-crosshairs'"></i>
                      Lokasi saya
                    </button>
                  </div>
                  <p v-if="errors.location" class="farm-field-error">{{ errors.location }}</p>
                </template>
              </div>
            </div>

            <div class="farm-form-card">
              <div class="farm-card-title-row">
                <h4 class="farm-card-title">Detail Pencarian</h4>
              </div>

              <div
                class="farm-api-status"
                :class="apiKeyConfigured && !apiKeyError && !mapAuthFailed && !mapError ? 'is-ready' : 'is-missing'"
              >
                <div class="farm-api-status-main">
                  <span class="farm-api-status-icon">
                    <i class="fa fa-key"></i>
                  </span>
                  <div>
                    <strong>Google Maps API Key</strong>
                    <p v-if="apiKeyLoading">Memuat status API Key...</p>
                    <p v-else-if="apiKeyError">Status API Key tidak dapat dimuat.</p>
                    <p v-else-if="mapAuthFailed">API Key tersimpan, tetapi ditolak Google Maps.</p>
                    <p v-else-if="mapError">API Key tersimpan, tetapi Google Maps gagal dimuat.</p>
                    <p v-else-if="apiKeyConfigured">Sudah diatur dan siap digunakan.</p>
                    <p v-else>Belum diatur. Atur API Key sebelum memulai pencarian.</p>
                  </div>
                </div>
                <router-link
                  to="/gmapkey"
                  class="btn btn-sm"
                  :class="apiKeyConfigured ? 'btn-white' : 'btn-primary'"
                >
                  {{ apiKeyConfigured ? "Ubah" : "Atur" }}
                </router-link>
              </div>

              <div class="form-group farm-field farm-license-section">
                <div class="farm-field-label">Lisensi WhatsApp</div>

                <div v-if="licensesLoading" class="farm-loading-line">
                  <i class="fa fa-spinner fa-spin"></i>
                  Memeriksa lisensi berbayar...
                </div>

                <div v-else-if="licensesError" class="farm-inline-alert is-error">
                  <div class="farm-inline-alert-content">
                    <strong>Status lisensi tidak dapat dimuat</strong>
                    <span>{{ licensesError }}</span>
                  </div>
                  <button
                    type="button"
                    class="btn btn-white btn-sm"
                    :disabled="licensesLoading"
                    @click="loadLicenses"
                  >
                    <i class="fa fa-refresh"></i> Coba lagi
                  </button>
                </div>

                <div v-else-if="!selectedLicense" class="farm-empty-inline">
                  <p class="farm-empty-inline-title">
                    Panen Kontak membutuhkan minimal satu lisensi WhatsApp berbayar yang aktif.
                  </p>
                  <router-link to="/license/list" class="btn btn-primary btn-sm">
                    Lihat Lisensi
                  </router-link>
                </div>

                <div v-else class="farm-auto-license">
                  <span class="farm-auto-license-icon">
                    <i class="fa fa-whatsapp"></i>
                  </span>
                  <div class="farm-auto-license-body">
                    <span class="farm-auto-license-label">Lisensi WhatsApp</span>
                    <strong>{{ licenseOptionLabel(selectedLicense) }}</strong>
                    <div class="farm-auto-license-meta">
                      <span class="farm-badge is-paid">
                        <i class="fa fa-check"></i> Berbayar
                      </span>
                      <span class="farm-badge is-active">
                        {{ licenseStatusLabel(selectedLicense) }}
                      </span>
                    </div>
                  </div>
                </div>

                <p v-if="selectedLicense" class="farm-help">
                  Lisensi dipilih otomatis dari lisensi berbayar yang aktif.
                </p>
              </div>

              <div data-tour-step="farm-radius-keyword">
                <div class="form-group farm-field">
                  <label for="farm-radius">
                    Radius
                    <span class="text-danger">*</span>
                  </label>
                  <select
                    id="farm-radius"
                    v-model.number="radius"
                    class="form-control farm-select"
                    :class="{ 'is-invalid': errors.radius }"
                    @change="clearError('radius')"
                  >
                    <option v-for="option in radiusOptions" :key="option" :value="option">
                      {{ option }} km
                    </option>
                  </select>
                  <p class="farm-help">
                    Radius lebih besar dapat menghasilkan lebih banyak kontak (maks. 20 km).
                  </p>
                  <p v-if="errors.radius" class="farm-field-error">{{ errors.radius }}</p>
                </div>

                <div class="form-group farm-field">
                  <label for="farm-keyword">
                    Kata kunci bisnis
                    <span class="text-danger">*</span>
                  </label>
                  <input
                    id="farm-keyword"
                    v-model="form.keyword"
                    class="form-control farm-input"
                    :class="{ 'is-invalid': errors.keyword }"
                    maxlength="255"
                    type="text"
                    placeholder="Contoh: restoran, klinik, bengkel"
                    @input="clearError('keyword')"
                  />
                  <p v-if="errors.keyword" class="farm-field-error">{{ errors.keyword }}</p>
                </div>

                <div class="farm-advanced">
                  <button
                    type="button"
                    class="farm-advanced-toggle"
                    @click="showAdvanced = !showAdvanced"
                  >
                    <i class="fa" :class="showAdvanced ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                    Opsi lanjutan
                  </button>

                  <div v-if="showAdvanced" class="form-group farm-field farm-advanced-field">
                    <label for="farm-type">Tipe tempat (opsional)</label>
                    <input
                      id="farm-type"
                      v-model="form.type"
                      class="form-control farm-input"
                      maxlength="255"
                      type="text"
                      placeholder="Contoh: restaurant"
                    />
                    <p class="farm-help">
                      Kosongkan jika tidak yakin. Gunakan tipe tempat Google Maps jika diperlukan.
                    </p>
                  </div>
                </div>
              </div>

              <div data-tour-step="farm-search">
                <div v-if="errors.form" class="farm-inline-alert is-error">
                  <i class="fa fa-exclamation-circle"></i>
                  <span>{{ errors.form }}</span>
                </div>

                <p class="farm-save-note">
                  <i class="fa fa-info-circle"></i>
                  Hasil pencarian akan tersimpan di daftar Panen Kontak.
                </p>

                <button
                  type="button"
                  class="btn btn-primary btn-block farm-submit"
                  :disabled="isLoading || !canStartSearch"
                  :title="startButtonTitle"
                  @click="submit"
                >
                  <i v-if="isLoading" class="fa fa-spinner fa-spin"></i>
                  <i v-else class="fa fa-search"></i>
                  {{ isLoading ? "Memulai pencarian..." : "Mulai Pencarian" }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <v-tour
      name="farmContactTour"
      :steps="availableTourSteps"
      :options="tourOptions"
      :callbacks="tourCallbacks"
    ></v-tour>
  </div>
</template>

<script>
import LayoutHeader from "@/components/layouts/Header.vue";
import LayoutSidebar from "@/components/layouts/Sidebar.vue";
import {
  getApiErrorMessage,
  getFarmLicenseStatusLabel,
  isEligibleFarmLicense
} from "@/utils/gmapContact";
import {
  isGoogleMapsAuthFailed,
  loadGoogleMaps,
  subscribeGoogleMapsAuthFailure
} from "@/utils/googleMapsLoader";

const DEFAULT_CENTER = { lat: -6.1805113, lng: 106.8283831 };

export default {
  components: {
    LayoutHeader,
    LayoutSidebar
  },
  data() {
    return {
      showAdvanced: false,
      locating: false,
      isLoading: false,
      isPageActive: false,
      apiKeyLoading: true,
      apiKeyConfigured: false,
      apiKey: "",
      apiKeyError: "",
      mapLoading: false,
      mapReady: false,
      mapError: "",
      mapErrorCode: "",
      mapAuthFailed: false,
      mapAuthUnsubscribe: null,
      licenses: [],
      licensesLoading: true,
      licenseStatusChecked: false,
      licensesError: "",
      locationSelected: false,
      locationLabel: "",
      mapOptions: {
        disableDefaultUI: true,
        zoomControl: true
      },
      marker: { position: { lat: DEFAULT_CENTER.lat, lng: DEFAULT_CENTER.lng } },
      center: { lat: DEFAULT_CENTER.lat, lng: DEFAULT_CENTER.lng },
      radius: 5,
      radiusOptions: [1, 2, 3, 5, 10, 15, 20],
      pengali: 1000,
      form: {
        license_id: "",
        keyword: "",
        type: ""
      },
      errors: {
        keyword: "",
        location: "",
        radius: "",
        form: ""
      },
      tourChecked: false,
      tourId: "farmContact.search",
      tourVersion: "v2",
      availableTourSteps: [],
      tourOptions: {
        highlight: true,
        startTimeout: 250,
        labels: {
          buttonSkip: "Lewati",
          buttonPrevious: "Sebelumnya",
          buttonNext: "Selanjutnya",
          buttonStop: "Selesai"
        }
      },
      tourCallbacks: {
        onSkip: this.tourComplete,
        onFinish: this.tourComplete
      },
      tourSteps: [
        {
          target: '[data-tour-step="farm-location"]',
          header: {
            title: "1. Lokasi"
          },
          content:
            "Klik peta atau cari alamat untuk menentukan lokasi pencarian.",
          params: { placement: "bottom" }
        },
        {
          target: '[data-tour-step="farm-radius-keyword"]',
          header: {
            title: "2. Kata kunci dan radius"
          },
          content:
            "Masukkan jenis bisnis yang ingin ditemukan dan tentukan radius pencarian.",
          params: { placement: "bottom" }
        },
        {
          target: '[data-tour-step="farm-search"]',
          header: {
            title: "3. Mulai pencarian"
          },
          content:
            "Klik Mulai Pencarian untuk mengumpulkan kontak bisnis dari Google Maps.",
          params: { placement: "top" }
        }
      ]
    };
  },
  computed: {
    radiusInMeters() {
      return Number(this.radius || 0) * this.pengali;
    },
    selectedLicense() {
      return (
        this.licenses.find(
          license => String(license.id) === String(this.form.license_id)
        ) || null
      );
    },
    canStartSearch() {
      return (
        this.mapReady &&
        this.licenseStatusChecked &&
        !this.licensesError &&
        !!this.selectedLicense
      );
    },
    startButtonTitle() {
      if (!this.mapReady) {
        return "Google Maps belum siap.";
      }
      if (this.licensesLoading || !this.licenseStatusChecked) {
        return "Status lisensi belum dapat diverifikasi.";
      }
      if (this.licensesError) {
        return "Status lisensi gagal dimuat.";
      }
      if (!this.selectedLicense) {
        return "Tidak ada lisensi WhatsApp berbayar yang aktif.";
      }
      return "";
    }
  },
  watch: {
    "$route.query.id"() {
      this.selectLicenseAutomatically();
    }
  },
  created() {
    this.isPageActive = true;
    this.mapAuthUnsubscribe = subscribeGoogleMapsAuthFailure(() => {
      if (!this.isPageActive) {
        return;
      }
      this.mapAuthFailed = true;
      this.mapReady = false;
      this.mapLoading = false;
    });
    this.loadApiKey();
    this.loadLicenses();
  },
  beforeDestroy() {
    this.isPageActive = false;
    if (typeof this.mapAuthUnsubscribe === "function") {
      this.mapAuthUnsubscribe();
    }
  },
  methods: {
    async loadApiKey() {
      this.apiKeyLoading = true;
      this.apiKeyError = "";
      this.apiKey = "";
      this.apiKeyConfigured = false;
      this.mapReady = false;
      this.mapError = "";
      this.mapErrorCode = "";
      this.mapAuthFailed = false;
      this.errors.form = "";

      try {
        const response = await this.$store.dispatch("gmapKey/getGmapKey");
        if (!this.isPageActive) {
          return;
        }
        this.apiKey = String(
          response && response.data ? response.data : ""
        ).trim();
        this.apiKeyConfigured = !!this.apiKey;
      } catch (error) {
        this.apiKeyError = getApiErrorMessage(
          error,
          "Status API Key belum dapat dimuat. Periksa koneksi internet lalu coba lagi."
        );
      } finally {
        this.apiKeyLoading = false;
        this.maybeStartTour();
      }

      if (this.apiKeyConfigured) {
        await this.initializeMap(this.apiKey);
      }
    },
    async initializeMap(apiKey) {
      this.mapLoading = true;
      this.mapError = "";
      this.mapErrorCode = "";
      this.mapReady = false;

      try {
        await loadGoogleMaps(apiKey);

        if (!this.isPageActive) {
          return;
        }

        if (isGoogleMapsAuthFailed()) {
          this.handleMapAuthFailure();
          return;
        }

        this.mapReady = true;
      } catch (error) {
        this.mapErrorCode = (error && error.code) || "script-error";
        this.mapError = this.getMapErrorMessage(this.mapErrorCode);
      } finally {
        this.mapLoading = false;
        this.maybeStartTour();
      }
    },
    getMapErrorMessage(code) {
      if (code === "key-changed") {
        return "Google Maps sudah dimuat dengan API Key sebelumnya. Muat ulang halaman agar API Key terbaru digunakan.";
      }
      if (code === "browser-unavailable") {
        return "Google Maps hanya dapat dimuat di browser.";
      }
      if (code === "script-error") {
        return "Google Maps gagal dimuat dari server Google. Periksa koneksi internet lalu coba lagi.";
      }
      return "Google Maps gagal dimuat. Periksa API Key dan koneksi internet.";
    },
    handleMapAuthFailure() {
      this.mapAuthFailed = true;
      this.mapReady = false;
      this.mapLoading = false;
    },
    retryMapLoad() {
      if (this.mapErrorCode === "key-changed") {
        this.reloadPage();
        return;
      }
      if (!this.apiKey) {
        this.loadApiKey();
        return;
      }
      this.initializeMap(this.apiKey);
    },
    reloadPage() {
      window.location.reload();
    },
    async loadLicenses() {
      this.licensesLoading = true;
      this.licensesError = "";
      this.licenseStatusChecked = false;
      this.licenses = [];
      this.form.license_id = "";
      this.errors.form = "";

      try {
        const response = await this.$store.dispatch("license/getLicenseList", {
          license_name: "",
          license_phone_number: "",
          license_status: "",
          page: 0,
          perPage: 1000
        });
        if (!this.isPageActive) {
          return;
        }

        const rows =
          response && response.data && Array.isArray(response.data.data)
            ? response.data.data
            : [];

        this.licenses = rows.filter(isEligibleFarmLicense);
        this.licenseStatusChecked = true;
        this.selectLicenseAutomatically();
      } catch (error) {
        this.licenses = [];
        this.licensesError = getApiErrorMessage(
          error,
          "Status lisensi tidak dapat dimuat. Periksa koneksi internet lalu coba lagi."
        );
      } finally {
        this.licensesLoading = false;
        this.maybeStartTour();
      }
    },
    selectLicenseAutomatically() {
      const queryId = this.$route.query.id;
      let selected = null;

      if (queryId) {
        selected = this.licenses.find(
          license => String(license.id) === String(queryId)
        );
      }

      // Backend mengurutkan lisensi berdasarkan status online, unlimited,
      // masa aktif, lalu tanggal dibuat. Gunakan kandidat pertama yang lolos
      // filter agar pemilihan tetap deterministik dan konsisten.
      if (!selected) {
        selected = this.licenses[0] || null;
      }

      this.form.license_id = selected ? selected.id : "";
    },
    licenseOptionLabel(license) {
      if (!license) {
        return "";
      }
      const name =
        license.license_name ||
        license.license_phone_number ||
        "Lisensi WhatsApp";
      if (
        license.license_phone_number &&
        name !== license.license_phone_number
      ) {
        return name + " - " + license.license_phone_number;
      }
      return name;
    },
    licenseStatusLabel(license) {
      return getFarmLicenseStatusLabel(license);
    },
    clearError(field) {
      if (Object.prototype.hasOwnProperty.call(this.errors, field)) {
        this.errors[field] = "";
      }
      this.errors.form = "";
    },
    validate() {
      this.errors = {
        keyword: "",
        location: "",
        radius: "",
        form: ""
      };

      let isValid = true;
      const keyword = String(this.form.keyword || "").trim();
      const radius = Number(this.radius);

      if (!this.apiKeyConfigured) {
        this.errors.form = this.apiKeyError
          ? "Status Google Maps API Key tidak dapat dipastikan. Coba lagi terlebih dahulu."
          : "Atur Google Maps API Key terlebih dahulu sebelum memulai pencarian.";
        isValid = false;
      } else if (!this.mapReady) {
        this.errors.form = this.mapAuthFailed
          ? "Google Maps menolak API Key. Periksa konfigurasi API Key terlebih dahulu."
          : "Google Maps belum siap. Periksa API Key dan koneksi internet.";
        isValid = false;
      }

      if (!this.licenseStatusChecked) {
        this.errors.form =
          this.licensesError ||
          "Status lisensi belum dapat diverifikasi. Coba muat ulang data lisensi.";
        isValid = false;
      } else if (!this.selectedLicense) {
        this.errors.form =
          "Panen Kontak membutuhkan minimal satu lisensi WhatsApp berbayar yang aktif.";
        isValid = false;
      }

      if (!this.locationSelected) {
        this.errors.location =
          "Pilih lokasi pada peta atau melalui pencarian lokasi.";
        isValid = false;
      }

      if (!Number.isFinite(radius) || radius < 1 || radius > 20) {
        this.errors.radius = "Radius harus antara 1 sampai 20 km.";
        isValid = false;
      }

      if (!keyword) {
        this.errors.keyword = "Kata kunci bisnis wajib diisi.";
        isValid = false;
      }

      return isValid;
    },
    submit() {
      if (this.isLoading || !this.canStartSearch) {
        return;
      }
      if (!this.validate()) {
        return;
      }

      this.isLoading = true;
      this.errors.form = "";

      const payload = {
        license_id: this.form.license_id,
        keyword: String(this.form.keyword || "").trim(),
        location: {
          lat: this.marker.position.lat,
          lng: this.marker.position.lng
        },
        radius: Math.round(Number(this.radius) * this.pengali),
        type: String(this.form.type || "").trim()
      };

      this.$store
        .dispatch("gmapKey/createHeader", payload)
        .then(() => {
          this.$fire({
            title: "Pencarian dimulai",
            text: "Hasil pencarian akan muncul di daftar Panen Kontak.",
            type: "success",
            timer: 2200
          });
          this.$router.push("/farm-contact/list");
        })
        .catch(error => {
          this.errors.form = getApiErrorMessage(
            error,
            "Pencarian gagal dimulai. Silakan coba lagi."
          );
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    coordinateLabel(lat, lng) {
      return Number(lat).toFixed(5) + ", " + Number(lng).toFixed(5);
    },
    setLocation(lat, lng, label) {
      this.marker.position = { lat: lat, lng: lng };
      this.center = { lat: lat, lng: lng };
      this.locationSelected = true;
      this.locationLabel = label || this.coordinateLabel(lat, lng);
      this.errors.location = "";
    },
    panToMarker() {
      if (this.$refs.mapRef && this.$refs.mapRef.panTo) {
        this.$refs.mapRef.panTo(this.marker.position);
      }
    },
    handleMapClick(event) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      this.setLocation(lat, lng, this.coordinateLabel(lat, lng));
    },
    handleMarkerDrag(event) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      this.setLocation(lat, lng, this.coordinateLabel(lat, lng));
    },
    setPlace(place) {
      if (!place || !place.geometry || !place.geometry.location) {
        return;
      }
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      const label =
        place.formatted_address ||
        place.name ||
        this.coordinateLabel(lat, lng);
      this.setLocation(lat, lng, label);
      this.panToMarker();
    },
    useCurrentLocation() {
      if (!navigator.geolocation) {
        this.errors.location =
          "Browser Anda tidak mendukung deteksi lokasi. Pilih lokasi pada peta.";
        return;
      }

      this.locating = true;
      navigator.geolocation.getCurrentPosition(
        position => {
          this.locating = false;
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          this.setLocation(lat, lng, "Lokasi saat ini");
          this.panToMarker();
        },
        () => {
          this.locating = false;
          this.errors.location =
            "Lokasi saat ini tidak dapat diambil. Pilih lokasi pada peta.";
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    },
    maybeStartTour() {
      if (
        !this.isPageActive ||
        this.tourChecked ||
        this.apiKeyLoading ||
        this.licensesLoading ||
        !this.mapReady
      ) {
        return;
      }

      this.tourChecked = true;
      if (!this.hasSeenTourVersion()) {
        this.tourShow();
      }
    },
    tourShow() {
      this.$nextTick(() => {
        this.availableTourSteps = this.tourSteps.filter(step => {
          const element = document.querySelector(step.target);
          return !!(
            element &&
            (element.offsetWidth ||
              element.offsetHeight ||
              element.getClientRects().length)
          );
        });

        if (!this.availableTourSteps.length) {
          return;
        }

        this.$nextTick(() => {
          if (this.$tours && this.$tours.farmContactTour) {
            this.$tours.farmContactTour.start();
          }
        });
      });
    },
    tourStorageKey() {
      const session =
        this.$store.getters.agentSession ||
        this.$store.state.login.memberSession ||
        {};
      const account =
        session.license_id ||
        session.network_id ||
        session.id ||
        session.member_id ||
        "member";
      return (
        "waajo.tour." +
        this.tourId +
        "." +
        this.tourVersion +
        "." +
        account
      );
    },
    hasSeenTourVersion() {
      try {
        return window.localStorage.getItem(this.tourStorageKey()) === "done";
      } catch (error) {
        return false;
      }
    },
    markTourVersionComplete() {
      try {
        window.localStorage.setItem(this.tourStorageKey(), "done");
      } catch (error) {
        // Tour tetap selesai walau browser memblokir localStorage.
      }
    },
    tourComplete() {
      // Tour Panen Kontak bersifat lokal; tidak ada penyimpanan ke database.
      this.markTourVersionComplete();
    }
  },
  name: "farmContactNearbySearch"
};
</script>

<style scoped>
.farm-contact-search /deep/ .page-wrapper {
  background: #f8fafc;
}

.farm-page-header {
  margin-bottom: 22px;
}

.farm-page-subtitle {
  margin: 6px 0 0;
  color: #667085;
  font-size: 14px;
  line-height: 1.5;
}

.farm-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.farm-layout {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.farm-map-card,
.farm-form-card {
  background: #ffffff;
  border: 1px solid #e8edf3;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.04);
}

.farm-map-card {
  flex: 1 1 56%;
  min-width: 0;
  padding: 20px;
}

.farm-form-card {
  flex: 1 1 44%;
  min-width: 0;
  padding: 22px;
}

.farm-card-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.farm-card-title {
  margin: 0;
  color: #172033;
  font-size: 16px;
  font-weight: 700;
}

.farm-selected-location {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 62%;
  padding: 5px 9px;
  color: #b45309;
  background: #fff7ef;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.farm-selected-location i {
  flex: 0 0 auto;
}

.farm-selected-location-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.farm-map-area {
  min-height: 360px;
}

.farm-map-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 390px;
  padding: 32px 24px;
  text-align: center;
  background: #fbfcfe;
  border: 1px dashed #d0d5dd;
  border-radius: 10px;
}

.farm-map-state > i {
  margin-bottom: 12px;
  color: #98a2b3;
  font-size: 28px;
}

.farm-map-state.is-error > i {
  color: #d92d20;
}

.farm-map-state.is-warning > i {
  color: #f99a43;
}

.farm-map-state h5 {
  margin: 0 0 6px;
  color: #172033;
  font-size: 15px;
  font-weight: 700;
}

.farm-map-state p {
  max-width: 380px;
  margin: 0 0 14px;
  color: #667085;
  font-size: 13px;
  line-height: 1.6;
}

.farm-map-state-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}

.farm-map-state .btn {
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
}

.farm-map-wrap {
  overflow: hidden;
  border: 1px solid #eef2f6;
  border-radius: 10px;
}

.farm-map {
  width: 100%;
  height: 430px;
}

.farm-map-help {
  margin: 10px 0 14px;
  color: #98a2b3;
  font-size: 12px;
}

.farm-location-search {
  display: flex;
  align-items: center;
  gap: 8px;
}

.farm-autocomplete {
  flex: 1 1 auto;
  min-width: 0;
}

.farm-location-button {
  flex: 0 0 auto;
  height: 38px;
  border-color: #d0d5dd;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
}

.farm-field {
  margin-bottom: 18px;
}

.farm-field label,
.farm-field-label {
  color: #344054;
  font-size: 13px;
  font-weight: 700;
}

.farm-field-label {
  display: block;
  margin-bottom: 7px;
}

.farm-input,
.farm-select {
  height: 42px;
  border-color: #d0d5dd;
  border-radius: 9px;
  font-size: 13px;
}

.farm-input:focus,
.farm-select:focus {
  border-color: #f99a43;
  box-shadow: 0 0 0 3px rgba(249, 154, 67, 0.15);
}

.farm-input.is-invalid,
.farm-select.is-invalid {
  border-color: #f04438;
}

.farm-help {
  margin: 6px 0 0;
  color: #98a2b3;
  font-size: 12px;
  line-height: 1.5;
}

.farm-field-error {
  margin: 6px 0 0;
  color: #d92d20;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
}

.farm-api-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
  padding: 12px;
  border: 1px solid transparent;
  border-radius: 10px;
}

.farm-api-status.is-ready {
  background: #f6fef9;
  border-color: #d1fadf;
}

.farm-api-status.is-missing {
  background: #fffcf5;
  border-color: #fedf89;
}

.farm-api-status-main {
  display: flex;
  align-items: flex-start;
  gap: 9px;
}

.farm-api-status-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  color: #b45309;
  background: #fff7ef;
  border-radius: 8px;
}

.farm-api-status strong {
  display: block;
  color: #344054;
  font-size: 12px;
  font-weight: 700;
}

.farm-api-status p {
  margin: 3px 0 0;
  color: #667085;
  font-size: 12px;
  line-height: 1.4;
}

.farm-api-status .btn {
  flex: 0 0 auto;
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
}

.farm-loading-line {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 0;
  color: #667085;
  font-size: 13px;
}

.farm-auto-license {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px;
  background: #fbfcfe;
  border: 1px solid #e8edf3;
  border-radius: 10px;
}

.farm-auto-license-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  color: #067647;
  background: #ecfdf3;
  border-radius: 9px;
  font-size: 16px;
}

.farm-auto-license-body {
  min-width: 0;
}

.farm-auto-license-label {
  display: block;
  margin-bottom: 2px;
  color: #98a2b3;
  font-size: 11px;
  font-weight: 600;
}

.farm-auto-license-body strong {
  display: block;
  color: #172033;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
}

.farm-auto-license-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 7px;
}

.farm-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}

.farm-badge.is-paid {
  color: #067647;
  background: #ecfdf3;
}

.farm-badge.is-active {
  color: #b54708;
  background: #fffaeb;
}

.farm-license-phone {
  color: #667085;
  font-size: 12px;
}

.farm-empty-inline {
  padding: 14px;
  background: #f8fafc;
  border: 1px dashed #d0d5dd;
  border-radius: 10px;
}

.farm-empty-inline-title {
  margin: 0 0 4px;
  color: #344054;
  font-size: 13px;
  font-weight: 700;
}

.farm-empty-inline .btn {
  margin-top: 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
}

.farm-advanced {
  margin-bottom: 14px;
}

.farm-advanced-toggle {
  padding: 0;
  color: #667085;
  background: transparent;
  border: 0;
  font-size: 12px;
  font-weight: 600;
}

.farm-advanced-toggle:hover {
  color: #b45309;
}

.farm-advanced-field {
  margin-top: 12px;
  margin-bottom: 0;
}

.farm-inline-alert {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 14px;
  padding: 10px 12px;
  border-radius: 9px;
  font-size: 12px;
  line-height: 1.5;
}

.farm-inline-alert.is-error {
  color: #b42318;
  background: #fef3f2;
  border: 1px solid #fecdca;
}

.farm-inline-alert-content {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
}

.farm-inline-alert-content strong {
  margin-bottom: 2px;
  font-size: 12px;
}

.farm-inline-alert .btn {
  flex: 0 0 auto;
  height: 30px;
  padding: 0 10px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 700;
}

.farm-save-note {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin: 0 0 12px;
  color: #667085;
  font-size: 12px;
  line-height: 1.5;
}

.farm-save-note i {
  margin-top: 2px;
  color: #f99a43;
}

.farm-submit {
  height: 44px;
  border-radius: 9px;
  font-size: 14px;
  font-weight: 700;
}

@media (max-width: 1199.98px) {
  .farm-layout {
    flex-direction: column;
  }

  .farm-map-card,
  .farm-form-card {
    width: 100%;
  }

  .farm-map {
    height: 360px;
  }
}

@media (max-width: 575.98px) {
  .farm-header-actions {
    margin-top: 12px;
  }

  .farm-header-actions .btn {
    flex: 1 1 auto;
  }

  .farm-map-card,
  .farm-form-card {
    padding: 16px;
  }

  .farm-map {
    height: 300px;
  }

  .farm-location-search {
    flex-direction: column;
    align-items: stretch;
  }

  .farm-selected-location {
    max-width: 100%;
  }

  .farm-card-title-row {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/farmContact/nearbySearch.vue